import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { get } from '../../helpers/api_helper';

function TopPickerCrown({ label }) {
  return (
    <svg
      className="today-picker-tile__top-crown"
      viewBox="0 0 132 96"
      role="img"
      aria-label={label}
    >
      <path className="crown-shadow" d="M19 77h94l-7 13H27Z" />
      <path className="crown-body" d="m18 25 27 25L66 8l21 42 27-25-10 55H28Z" />
      <path className="crown-highlight" d="m32 39 15 18 18-31 5 12-18 30H35Z" />
      <path className="crown-band" d="M27 67h78l-2 15H29Z" />
      <circle className="crown-gem crown-gem--pink" cx="43" cy="74" r="5" />
      <circle className="crown-gem crown-gem--blue" cx="66" cy="74" r="6" />
      <circle className="crown-gem crown-gem--pink" cx="89" cy="74" r="5" />
      <circle className="crown-tip" cx="18" cy="23" r="7" />
      <circle className="crown-tip" cx="66" cy="8" r="7" />
      <circle className="crown-tip" cx="114" cy="23" r="7" />
    </svg>
  );
}

TopPickerCrown.propTypes = {
  label: PropTypes.string.isRequired,
};

function ActivePickersWidget({ refreshKey, t }) {
  const [pickers, setPickers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function loadPickers() {
      try {
        const response = await get('/api/today/active-pickers');
        if (mounted) {
          setPickers(Array.isArray(response.pickers) ? response.pickers : []);
          setLoading(false);
          setError(false);
        }
      } catch (requestError) {
        if (mounted) {
          setLoading(false);
          setError(true);
        }
      }
    }

    loadPickers();
    return () => {
      mounted = false;
    };
  }, [refreshKey]);

  return (
    <div className="today-active-pickers">
      <header className="today-active-pickers__header">
        <div>
          <span>{t('Warehouse floor')}</span>
          <h2>{t('Active Pickers')}</h2>
        </div>
        <strong>{pickers.length}</strong>
      </header>

      {loading && <div className="today-active-pickers__message">{t('Loading active routes…')}</div>}
      {error && <div className="today-active-pickers__message is-error">{t('Active pickers unavailable')}</div>}
      {!loading && !error && pickers.length === 0 && (
        <div className="today-active-pickers__message">{t('No routes are being picked')}</div>
      )}

      {!loading && !error && pickers.length > 0 && (
        <div className="today-active-pickers__grid">
          {pickers.map((picker) => (
            <article className="today-picker-tile" key={picker.pickroute_id}>
              {picker.is_top_picker_today && (
                <TopPickerCrown
                  label={t('Most stops picked today: {{count}}', {
                    count: picker.stops_picked_today,
                  })}
                />
              )}
              <div className="today-picker-tile__person">
                <i className="mdi mdi-account-hard-hat" aria-hidden="true" />
                <strong title={picker.picker}>{picker.picker}</strong>
              </div>
              <div className="today-picker-tile__route">
                <span>{`#${picker.pickroute_id}`}</span>
                <span title={picker.pickroute_type}>{picker.pickroute_type}</span>
              </div>
              <div className="today-picker-tile__next">
                <small>{t('Next stop')}</small>
                <strong>{picker.next_location || t('Route complete')}</strong>
              </div>
              <div
                className="today-picker-tile__progress"
                title={t('{{picked}} of {{total}} route stops picked', {
                  picked: picker.route_stops_completed,
                  total: picker.total_stops,
                })}
              >
                <span>{t('Stop')}</span>
                <strong>{`${picker.route_stops_completed}/${picker.total_stops}`}</strong>
              </div>
              <div
                className="today-picker-tile__progress-bar"
                role="progressbar"
                aria-label={t('Route progress')}
                aria-valuemin="0"
                aria-valuemax={picker.total_stops}
                aria-valuenow={picker.route_stops_completed}
              >
                <span
                  style={{
                    width: `${picker.total_stops > 0
                      ? Math.min(100, (picker.route_stops_completed / picker.total_stops) * 100)
                      : 0}%`,
                  }}
                />
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

ActivePickersWidget.defaultProps = {
  t: (value) => value,
};

ActivePickersWidget.propTypes = {
  refreshKey: PropTypes.number.isRequired,
  t: PropTypes.func,
};

export default withTranslation()(ActivePickersWidget);
